import { TimeOfDay } from '@app/enums/timeOfDay.enum';
import { AppointmentRequestService } from '@app/services/appointment-request.service';
import { dateToStringNoTimeZone } from '@app/shared/helpers/extensions';
import moment from 'moment';
import { AppointmentRequest } from './appointmentRequest.model';
import { Client } from './client.model';
import { CreditCard } from './creditCard.model';
import { DateTimeOfDay } from './dateTimeOfDay.model';
import { Pet } from './pet.model';

export { DateTimeOfDay };

export class SyncAppointmentRequestDataRequest {
    appointmentRequest: AppointmentRequestModel;
    localDateTime: string;
    token: string;

    constructor(appointmentRequestModel: AppointmentRequestModel, localDateTime: string, token: string) {
        this.appointmentRequest = appointmentRequestModel;
        this.localDateTime = localDateTime;
        this.token = token;
    }

    public static convertLocalStorageApptRequestToSyncApptRequestDataRequest(
        localStorageApptRequest: AppointmentRequest,
        daySmartAccountID: string,
        appointmentRequestService: AppointmentRequestService,
        token: string,
        creditCard?: CreditCard
    ): SyncAppointmentRequestDataRequest {
        const lastModifiedDateTime = new Date();
        const dateTimeOfDayList = localStorageApptRequest.dateTimeOfDayList;
        let startDateTime: string;
        let endDateTime: string;
        if (dateTimeOfDayList?.length) {
            // Request-only flow: use today's date with time zeroed as placeholder
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            startDateTime = dateToStringNoTimeZone(today);
            const endDate = new Date(today.getTime() + 60000);
            endDateTime = dateToStringNoTimeZone(endDate);
        } else {
            const tempStartDateTime = new Date(localStorageApptRequest.datetime);
            startDateTime = dateToStringNoTimeZone(tempStartDateTime);
            const duration = appointmentRequestService.getAppointmentRequestDuration(localStorageApptRequest);
            endDateTime = dateToStringNoTimeZone(new Date(tempStartDateTime.getTime() + duration * 60000));
        }
        const appointmentRequestItemList: AppointmentRequestItem[] = [];
        let itemStartDateTime = new Date(startDateTime);
        let itemEndDateTime = new Date(itemStartDateTime);
        const isFirstAvailable =
            localStorageApptRequest.employee[0]?.id === '0' || localStorageApptRequest.employee[0]?.firstAvailable === true;
        for (const service of localStorageApptRequest.service) {
            const itemDuration = appointmentRequestService.getItemDuration(service, localStorageApptRequest.employee[0]);
            const resourceID = service.resourceIDList?.length ? service.resourceIDList[0] : undefined;
            itemEndDateTime = new Date(itemStartDateTime.getTime() + itemDuration * 60000);
            const employeeID = dateTimeOfDayList?.length && isFirstAvailable ? undefined : localStorageApptRequest.employee[0].id;
            const appointmentRequestItem = new AppointmentRequestItem(
                service.id,
                dateToStringNoTimeZone(itemStartDateTime),
                dateToStringNoTimeZone(itemEndDateTime),
                employeeID,
                resourceID
            );
            appointmentRequestItemList.push(appointmentRequestItem);
            itemStartDateTime = new Date(itemEndDateTime);
        }

        let client;
        if (localStorageApptRequest.petInfo !== undefined) {
            const pet = new PetModel(localStorageApptRequest.petInfo, lastModifiedDateTime, daySmartAccountID);
            client = new ClientModel(localStorageApptRequest.client, lastModifiedDateTime, daySmartAccountID, pet);
        } else {
            client = new ClientModel(localStorageApptRequest.client, lastModifiedDateTime, daySmartAccountID);
        }
        client.creditCard = creditCard;
        const comments = localStorageApptRequest.comments;
        const appointmentRequestModel = new AppointmentRequestModel(
            daySmartAccountID,
            client,
            startDateTime,
            endDateTime,
            AppointmentRequestStatuses.Pending,
            lastModifiedDateTime,
            appointmentRequestItemList,
            AppointmentRequestSources.Internal,
            comments,
            undefined,
            dateTimeOfDayList
        );
        const localDateTime = dateToStringNoTimeZone(moment().toDate());
        const syncAppointmentRequestDataRequest = new SyncAppointmentRequestDataRequest(appointmentRequestModel, localDateTime, token);
        return syncAppointmentRequestDataRequest;
    }
}
export class AppointmentRequestModel {
    public id?: string;
    public daySmartAccountID: string;
    public client?: ClientModel;
    public startDateTime: string;
    public endDateTime: string;
    public status: AppointmentRequestStatuses;
    public lastModifiedDateTime: Date;
    public itemList: AppointmentRequestItem[];
    public appointmentSource?: AppointmentRequestSources;
    public ticketID?: number;
    public comments?: string;
    public isRequestAppointmentOnly?: boolean;
    public dateTimeOfDayList?: { date: string; timeOfDay: number }[];

    constructor(
        daySmartAccountID: string,
        client: ClientModel,
        startDateTime: string,
        endDateTime: string,
        status: AppointmentRequestStatuses,
        lastModifiedDateTime: Date,
        itemList: AppointmentRequestItem[],
        appointmentSource: AppointmentRequestSources,
        comments?: string,
        id?: string,
        dateTimeOfDayList?: DateTimeOfDay[]
    ) {
        this.id = id;
        this.daySmartAccountID = daySmartAccountID;
        this.client = client;
        this.startDateTime = startDateTime;
        this.endDateTime = endDateTime;
        this.status = status;
        this.lastModifiedDateTime = lastModifiedDateTime;
        this.itemList = itemList;
        this.appointmentSource = appointmentSource;
        this.comments = comments;
        this.dateTimeOfDayList = dateTimeOfDayList?.map((item) => ({
            date: item.date,
            timeOfDay: timeOfDayToInt(item.timeOfDay),
        }));
        this.isRequestAppointmentOnly = dateTimeOfDayList?.length ? true : undefined;
    }
}
class ClientModel {
    public id?: string;
    public firstName: string;
    public lastName: string;
    public cell: string;
    public email: string;
    public onlineID?: string;
    public lastModifiedDateTime: Date;
    public daySmartAccountID?: string;
    public pet?: PetModel;
    public enableEmailMarketing: boolean;
    public enableTextMarketing: boolean;
    public enableTextNotifications: boolean;
    public creditCard?: CreditCard;
    public address1?: string;
    public address2?: string;
    public city?: string;
    public state?: string;
    public zipCode?: string;

    constructor(client: Client, lastModifiedDateTime: Date, daySmartAccountID: string, pet?: PetModel, creditCard?: CreditCard) {
        this.firstName = client.firstName;
        this.lastName = client.lastName;
        this.cell = client.phoneNumber;
        this.email = client.email;
        this.lastModifiedDateTime = lastModifiedDateTime;
        this.daySmartAccountID = daySmartAccountID;
        this.pet = pet;
        this.enableEmailMarketing = client.enableEmailMarketing;
        this.enableTextMarketing = client.enableTextMarketing;
        this.enableTextNotifications = client.enableTextNotifications;
        this.creditCard = creditCard;
        this.address1 = client.address1;
        this.address2 = client.address2;
        this.city = client.city;
        this.state = client.state;
        this.zipCode = client.zipCode;
        this.onlineID = client.onlineID;
    }
}
export class PetModel {
    public id?: string;
    public name: string;
    public type?: PetType;
    public breed?: PetBreed;
    public weight?: string;
    public clientID?: string;
    public daySmartAccountID?: string;
    public lastModifiedDateTime?: Date;

    constructor(pet: Pet, lastModifiedDateTime: Date, daySmartAccountID: string) {
        this.name = pet.name;
        if (pet.petType) {
            this.type = new PetType(pet.petType?.id, pet.petType?.name);
        }
        if (pet.petBreed) {
            this.breed = new PetBreed(pet.petBreed?.id, pet.petBreed?.name, pet.petBreed?.typeID);
        }
        if (pet.weight) {
            this.weight = pet.weight;
        }
        this.lastModifiedDateTime = lastModifiedDateTime;
        this.daySmartAccountID = daySmartAccountID;
    }
}
export class PetType {
    public id: string;
    public name: string;
    public daySmartAccountID?: string;
    public lastModifiedDateTime?: string;

    constructor(id: string, name: string) {
        this.id = id;
        this.name = name;
    }
}

export class PetBreed {
    public id: string;
    public name: string;
    public typeID: string;
    public daySmartAccountID?: string;
    public lastModifiedDateTime?: string;

    constructor(id: string, name: string, typeID: string) {
        this.id = id;
        this.name = name;
        this.typeID = typeID;
    }
}
export class AppointmentRequestItem {
    public id?: string;
    public appointmentRequestID?: string;
    public serviceID: string;
    public employeeID?: string;
    public resourceID?: string;
    public startDateTime: string;
    public endDateTime: string;

    constructor(
        serviceID: string,
        startDateTime: string,
        endDateTime: string,
        employeeID?: string,
        resourceID?: string,
        id?: string,
        appointmentRequestID?: string
    ) {
        this.id = id;
        this.appointmentRequestID = appointmentRequestID;
        this.serviceID = serviceID;
        if (employeeID) {
            this.employeeID = employeeID;
        }
        this.resourceID = resourceID;
        this.startDateTime = startDateTime;
        this.endDateTime = endDateTime;
    }
}
export enum AppointmentRequestSources {
    GoodFriend = 'goodfriend',
    Internal = 'internal',
}

export enum AppointmentRequestStatuses {
    Accepted = 'accepted',
    Pending = 'pending',
    Rejected = 'rejected',
    Closed = 'closed',
    Canceled = 'canceled',
    Voided = 'voided',
    NoShow = 'no-show',
    CheckedIn = 'checked-in',
}

export class SyncAppointmentRequestDataResponse {
    newValue?: AppointmentRequestModel;
    skipped?: boolean;
    oldValue?: AppointmentRequestModel;
}

function timeOfDayToInt(timeOfDay: TimeOfDay): number {
    switch (timeOfDay) {
        case TimeOfDay.Anytime:
            return 0;
        case TimeOfDay.Morning:
            return 1;
        case TimeOfDay.Afternoon:
            return 2;
        case TimeOfDay.Evening:
            return 3;
    }
}
